import React, { useState, useCallback } from 'react';
import {
  Text,
  Input,
  Button,
  Spinner,
  Label,
  tokens,
} from '@fluentui/react-components';
import {
  LockClosedRegular,
  KeyRegular,
  PersonRegular,
  ArrowRightRegular,
} from '@fluentui/react-icons';
import { connectionsService } from '../../api/connectionsService';
import { ConnectorIcon } from '../../utils/connectorIcons';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AuthStepProps {
  connectionId: string;
  authMethod: string;
  connectorId: string;
  connectorName: string;
  /** Optional connector logo URL to display instead of generic icon */
  logoUrl?: string | null;
  /** Called after successful auth (OAuth redirect or credential save) */
  onComplete: () => void;
  isReauthorization?: boolean;
}

/** Field descriptor for credential forms */
interface CredentialField {
  key: string;
  label: string;
  type: 'text' | 'password' | 'url';
  required: boolean;
  placeholder?: string;
}

// ---------------------------------------------------------------------------
// Credential field definitions per auth method
// ---------------------------------------------------------------------------

const CREDENTIAL_FIELDS: Record<string, CredentialField[]> = {
  api_key: [
    { key: 'api_key', label: 'API Key', type: 'password', required: true, placeholder: 'Enter your API key' },
  ],
  basic: [
    { key: 'username', label: 'Username', type: 'text', required: true, placeholder: 'Username' },
    { key: 'password', label: 'Password', type: 'password', required: true, placeholder: 'Password' },
  ],
  credentials: [
    { key: 'host', label: 'Host', type: 'text', required: true, placeholder: 'e.g. db.example.com' },
    { key: 'port', label: 'Port', type: 'text', required: false, placeholder: 'e.g. 5432' },
    { key: 'database', label: 'Database', type: 'text', required: true, placeholder: 'Database name' },
    { key: 'username', label: 'Username', type: 'text', required: true, placeholder: 'Username' },
    { key: 'password', label: 'Password', type: 'password', required: true, placeholder: 'Password' },
  ],
  service_account: [
    { key: 'service_account_json', label: 'Service Account JSON', type: 'password', required: true, placeholder: 'Paste JSON key contents' },
  ],
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const AuthStep: React.FC<AuthStepProps> = ({
  connectionId,
  authMethod,
  connectorId,
  connectorName,
  logoUrl,
  onComplete,
  isReauthorization = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formValues, setFormValues] = useState<Record<string, string>>({});

  // ----- OAuth flow -----
  const handleOAuthInitiate = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await connectionsService.initiateOAuth(connectionId);
      // Redirect the user to the OAuth authorization URL
      window.location.href = result.authorization_url;
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to start OAuth flow');
      setLoading(false);
    }
  }, [connectionId]);

  // ----- No-auth connection -----
  const handleNoAuthConnect = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const testResult = await connectionsService.testConnection(connectionId);
      if (!testResult.success) {
        setError(testResult.message || 'Connection test failed');
        return;
      }
      onComplete();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Connection test failed');
    } finally {
      setLoading(false);
    }
  }, [connectionId, onComplete]);

  // ----- Credential form submit -----
  const handleCredentialSubmit = useCallback(async () => {
    const fields = CREDENTIAL_FIELDS[authMethod] || [];
    // Validate required fields
    for (const field of fields) {
      if (field.required && !formValues[field.key]?.trim()) {
        setError(`${field.label} is required`);
        return;
      }
    }

    setLoading(true);
    setError(null);
    try {
      await connectionsService.updateConnection(connectionId, {
        credentials: formValues,
      });
      // After saving credentials, test the connection to transition status to 'connected'
      const testResult = await connectionsService.testConnection(connectionId);
      if (!testResult.success) {
        setError(testResult.message || 'Connection test failed');
        setLoading(false);
        return;
      }
      onComplete();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save credentials');
    } finally {
      setLoading(false);
    }
  }, [connectionId, authMethod, formValues, onComplete]);

  const updateField = useCallback((key: string, value: string) => {
    setFormValues((prev) => ({ ...prev, [key]: value }));
  }, []);

  // ----- No-auth UI -----
  if (authMethod === 'none') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, alignItems: 'center', padding: 32 }}>
        <Text size={500} weight="semibold">
          {isReauthorization ? 'Reauthorize' : 'Connect to'} {connectorName}
        </Text>
        <Text size={300} style={{ color: tokens.colorNeutralForeground3, textAlign: 'center', maxWidth: 450 }}>
          This development connector does not require or store credentials.
        </Text>
        {error && (
          <Text size={200} style={{ color: tokens.colorPaletteRedForeground1 }}>
            {error}
          </Text>
        )}
        <Button
          appearance="primary"
          size="large"
          disabled={loading}
          onClick={handleNoAuthConnect}
          icon={loading ? <Spinner size="tiny" /> : <ArrowRightRegular />}
          iconPosition="after"
        >
          {loading ? 'Connecting…' : 'Connect'}
        </Button>
      </div>
    );
  }

  // ----- OAuth UI -----
  if (authMethod === 'oauth' || authMethod === 'oauth2') {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 20, alignItems: 'center', padding: 32 }}>
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={connectorName}
            style={{ width: 64, height: 64, objectFit: 'contain' }}
          />
        ) : (
          <ConnectorIcon connectorId={connectorId} name={connectorName} size={64} />
        )}
        <Text size={500} weight="semibold">
          Connect to {connectorName}
        </Text>
        <Text size={300} style={{ color: tokens.colorNeutralForeground3, textAlign: 'center', maxWidth: 450 }}>
          You will be redirected to {connectorName} to authorize access. Your credentials are never stored on our servers — only encrypted tokens are saved.
        </Text>

        {error && (
          <Text size={200} style={{ color: tokens.colorPaletteRedForeground1 }}>
            {error}
          </Text>
        )}

        <Button
          appearance="primary"
          size="large"
          icon={loading ? <Spinner size="tiny" /> : <ArrowRightRegular />}
          iconPosition="after"
          disabled={loading}
          onClick={handleOAuthInitiate}
        >
          {loading ? 'Redirecting…' : `${isReauthorization ? 'Reauthorize' : 'Connect'} with ${connectorName}`}
        </Button>
      </div>
    );
  }

  // ----- File upload — handled by UploadView, skip here -----
  if (authMethod === 'file_upload') {
    return null;
  }

  // ----- Credential form UI -----
  const fields = CREDENTIAL_FIELDS[authMethod] || CREDENTIAL_FIELDS.api_key;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16, maxWidth: 480 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <KeyRegular style={{ fontSize: 24, color: tokens.colorBrandForeground1 }} />
        <Text size={500} weight="semibold">
          Enter Credentials
        </Text>
      </div>
      <Text size={300} style={{ color: tokens.colorNeutralForeground3 }}>
        Your credentials are encrypted before being stored. We never have access to your plaintext secrets.
      </Text>

      {fields.map((field) => (
        <div key={field.key} style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Label required={field.required} htmlFor={`auth-${field.key}`}>
            {field.label}
          </Label>
          <Input
            id={`auth-${field.key}`}
            type={field.type === 'password' ? 'password' : 'text'}
            placeholder={field.placeholder}
            value={formValues[field.key] || ''}
            onChange={(_, data) => updateField(field.key, data.value)}
            contentBefore={field.type === 'password' ? <LockClosedRegular /> : <PersonRegular />}
          />
        </div>
      ))}

      {error && (
        <Text size={200} style={{ color: tokens.colorPaletteRedForeground1 }}>
          {error}
        </Text>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        <Button
          appearance="primary"
          disabled={loading}
          onClick={handleCredentialSubmit}
          icon={loading ? <Spinner size="tiny" /> : undefined}
        >
          {loading ? 'Saving…' : 'Save & Continue'}
        </Button>
      </div>
    </div>
  );
};

export default AuthStep;